import { useState } from 'react'
import { useTranslation } from 'react-i18n-lite'

import { concatenateDisplayText } from 'shared/utils/object/concatenateFields'
import {
  Badge,
  DataTableSkeletonDots,
  SortButton,
  TableHeaderColumn
} from '../DataTable.styles'
import useTableContext from '../hooks/useTableContext'
import {
  ArrowsUpAndDown,
  AscendingSortArrow,
  DescendingSortArrow
} from '../SVG'
import {
  getColumnSortInfo,
  calculateNextSortDirection,
  createSortOptions
} from '../helpers/sorting'

type TableHeaderProps = {
  isLoading: boolean
}

function TableHeader({ isLoading }: TableHeaderProps) {
  const { t } = useTranslation()

  const {
    mode,
    fields,
    actionColumns,
    actionColumnsPosition,
    columnsNames,
    columnsLayouts,
    sortOptions,
    onSort,
    informational,
    actionButton,
    embedded,
    hideRowActionButtons,
    hideSortButtons,
    concatFields,
    customActionButtons,
    insertOnly
  } = useTableContext()

  const renderSortIcon = (
    direction: string,
    shouldShowBadge: boolean,
    sortOrderNumber: number | null
  ) => {
    return (
      <>
        {shouldShowBadge && <Badge>{sortOrderNumber}</Badge>}
        {direction === 'asc' && <AscendingSortArrow />}
        {direction === 'desc' && <DescendingSortArrow />}
        {direction === '' && <ArrowsUpAndDown />}
      </>
    )
  }

  const renderContentCell = (columnName: string) => {
    const { currentDirection, sortOrderNumber, shouldShowBadge } =
      getColumnSortInfo(sortOptions, columnName)
    const columnLayout = columnsLayouts.find(
      (layout) => layout.name === columnName
    )
    const columnWidth = columnLayout?.width || ''

    if (isLoading) {
      return (
        <TableHeaderColumn
          key={columnName}
          style={{ width: columnWidth }}
          mode={mode}
        >
          <DataTableSkeletonDots />
        </TableHeaderColumn>
      )
    }

    const columnDisplayText = concatenateDisplayText({
      concatFields,
      fields,
      columnName
    })

    const canShowSortButtons =
      mode === 'view' &&
      fields?.[columnName]?.type !== 'RELATION' &&
      !hideSortButtons

    const handleSortClick = (event: React.MouseEvent) => {
      const isMultipleSort = event.ctrlKey || event.metaKey

      const nextDirection = calculateNextSortDirection(currentDirection)

      const newSortOptions = createSortOptions(
        sortOptions,
        columnName,
        nextDirection,
        isMultipleSort
      )

      onSort(newSortOptions, false)
    }

    return (
      <TableHeaderColumn
        key={columnName}
        style={{ width: columnWidth }}
        mode={mode}
      >
        {canShowSortButtons && (
          <SortButton
            role='button'
            onClick={handleSortClick}
            data-testid={`sort-${columnName}-${currentDirection}`}
          >
            {renderSortIcon(currentDirection, shouldShowBadge, sortOrderNumber)}
          </SortButton>
        )}
        {columnDisplayText}
      </TableHeaderColumn>
    )
  }

  const renderActionCells = () => {
    const shouldShowActionColumn =
      !embedded ||
      actionButton?.type === 'document' ||
      !informational ||
      !!customActionButtons

    if (hideRowActionButtons === true || !shouldShowActionColumn) {
      return []
    }

    return actionColumns.map((column) => (
      <TableHeaderColumn
        style={{ width: '5%' }}
        className={mode === 'view' ? 'text-center' : ''}
        key={column.title}
        mode={mode}
      >
        {isLoading ? (
          <DataTableSkeletonDots />
        ) : (
          t(`data-tables.${column.title}`)
        )}
      </TableHeaderColumn>
    ))
  }

  const renderSelectCell = () => {
    if (informational || mode !== 'view') return null

    return (
      <TableHeaderColumn style={{ width: '5%' }} key='select-cell' mode={mode}>
        {isLoading ? <DataTableSkeletonDots /> : t(`data-tables.select`)}
      </TableHeaderColumn>
    )
  }

  if (!fields || insertOnly) {
    return null
  }

  const contentCells = columnsNames.map(renderContentCell)
  const actionCells = renderActionCells()
  const selectCell = renderSelectCell()

  let headerCells: JSX.Element[]
  if (actionColumnsPosition === 'first') {
    headerCells = [selectCell, ...actionCells, ...contentCells].filter(
      Boolean
    ) as JSX.Element[]
  } else {
    headerCells = [selectCell, ...contentCells, ...actionCells].filter(
      Boolean
    ) as JSX.Element[]
  }

  return (
    <thead>
      <tr>{headerCells}</tr>
    </thead>
  )
}

export default TableHeader
